import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "api";
import { useOpekepeApplicationYears } from "stores/persistent-stores/useOpekepeApplicationYears";
import { useFieldsContext, useFieldsContextActions } from "context/FieldsProvider";
import { useAssociateFarmers } from "hooks/useAssociates";
import { useFetchStateHelper, useFetchStateMerger } from "hooks/useFetchState";
import {
  populateFarmers,
  populateFields,
  transformFields,
} from "./utils/populate-utils";
import { filterFields, filterFarmers } from "./utils/filter-utils";

export const useKydData = (filters, category) => {
  const { t } = useTranslation();

  const [filteredData, setFilteredData] = useState([]);
  const [dataCount, setDataCount] = useState({
    fields: 0,
    farmers: 0,
  });

  const { latestActiveYear } = useOpekepeApplicationYears();
  const { associateFarmers } = useAssociateFarmers();
  const { fields, fetchState: fieldsFetchState } = useFieldsContext();
  const { data: farmers, ...queryResponse } = useQuery({
    queryKey: ["my-farmers"],
    queryFn: api.users.fetchMyFarmers,
  });

  const { fetchState: farmersFetchState } = useFetchStateHelper(queryResponse);
  const { fetchState, setFetchState } = useFetchStateMerger(
    fieldsFetchState,
    farmersFetchState
  );

  const transformedFields = useMemo(() => {
    return transformFields(fields, t);
  }, [fields, t]);

  const myFarmers = useMemo(
    () => populateFarmers(farmers, associateFarmers, latestActiveYear),
    [farmers, associateFarmers, latestActiveYear]
  );

  const myFields = useMemo(
    () => populateFields(transformedFields, farmers),
    [transformedFields, farmers]
  );

  useEffect(() => {
    setFetchState.toLoading();

    const farmersFiltered = filterFarmers(myFarmers, filters);
    const fieldsFiltered = filterFields(myFields, filters);

    setFilteredData({
      fields: fieldsFiltered,
      farmers: farmersFiltered,
    });

    setDataCount({
      fields: fieldsFiltered.length,
      farmers: farmersFiltered.length,
    });

    setFetchState.toSuccess();

    const timeout = setTimeout(() => {
      setFetchState.toSuccess();
    }, 300);

    return () => clearTimeout(timeout);
  }, [myFarmers, myFields, filters, category, setFetchState]);

  return {
    dataCount,
    fields: filteredData.fields,
    farmers: filteredData.farmers,
    filteredData:
      category === "farmers" ? filteredData.farmers : filteredData.fields,
    fetchState,
  };
};

export const useSavedKydFilters = () => {
  const queryClient = useQueryClient();

  const { data: savedFilterGroups, ...queryResponse } = useQuery({
    queryKey: ["saved-filters"],
    queryFn: api.filters.dashboard.fetchAll,
  });

  const { fetchState } = useFetchStateHelper(queryResponse);

  const { mutateAsync: saveNewFilterGroup, ...saveResponse } = useMutation({
    mutationKey: ["save-new-filter"],
    mutationFn: (payload) => api.filters.dashboard.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-filters"] });
    },
  });
  const { fetchState: saveFetchState } = useFetchStateHelper(saveResponse);

  const { mutateAsync: editFilterGroup, ...editResponse } = useMutation({
    mutationKey: ["edit-filter"],
    mutationFn: ({ id, payload }) => api.filters.dashboard.edit(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-filters"] });
    },
  });
  const { fetchState: editFetchState } = useFetchStateHelper(editResponse);

  const { mutateAsync: deleteFilterGroup, ...deleteResponse } = useMutation({
    mutationKey: ["edit-filter"],
    mutationFn: (id) => api.filters.dashboard.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-filters"] });
    },
  });
  const { fetchState: deleteFetchState } = useFetchStateHelper(deleteResponse);

  const { fetchState: actionsFetchState } = useFetchStateMerger(
    saveFetchState,
    editFetchState,
    deleteFetchState
  );

  return {
    savedFilterGroups,
    fetchState,
    actionsFetchState,
    saveNewFilterGroup,
    editFilterGroup,
    deleteFilterGroup,
  };
};

export const useKydFarmerFields = (userId, year) => {
  const { data: fields, ...queryResponse } = useQuery({
    queryKey: ["kyd-farmer-fields", userId, year],
    queryFn: () => api.opekepe.fetchFarmerFields({ userId, year }),
    enabled: Boolean(userId && year),
  });
  const { fetchState } = useFetchStateHelper(queryResponse);

  return { fields, fetchState };
};

export const useKydFarmerFieldsActions = () => {
  const queryClient = useQueryClient();
  const { fetchUserFields } = useFieldsContextActions();

  const { mutateAsync: saveFarmerFields, ...saveResponse } = useMutation({
    mutationKey: ["kyd-farmer-fields-save"],
    mutationFn: async ({
      userId,
      year,
      newFields,
      keptFieldIds,
      removedFieldIds,
      spdFieldIds,
    }) => {
      const createdIds = [];
      const createdSpdIds = [];
      for (const { isSpd, ...payload } of newFields) {
        const { data } = await api.fields.create(payload, { user_id: userId });
        createdIds.push(data.id);
        if (isSpd) {
          createdSpdIds.push(data.id);
        }
      }
      return api.opekepe.importFields({
        userId,
        year,
        fieldIds: [...keptFieldIds, ...createdIds],
        removedFieldIds,
        spdFieldIds: [...spdFieldIds, ...createdSpdIds],
      });
    },
    onSuccess: () => {
      fetchUserFields();
      queryClient.invalidateQueries({ queryKey: ["kyd-farmer-fields"] });
      return queryClient.invalidateQueries({ queryKey: ["my-farmers"] });
    },
  });
  const { fetchState } = useFetchStateHelper(saveResponse);

  return { saveFarmerFields, fetchState };
};
